import React, {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useLocation} from 'react-router-dom';
import ChatPanel from './ChatPanel';
import {Box} from '@mui/material';
import html2canvas from 'html2canvas';
import {setChatOpen, setHistoryPanelOpen} from '../../features/Insights/chatSlice';

function ChatWrapper() {
  const location = useLocation();
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => {
    return state.chat.isOpen;
  });
  const overlayDimEnabled = useSelector((state) => {
    return state.chat.overlayDimEnabled;
  });
  const authenticationStatus = useSelector((state) => {
    return state.app.authenticationStatus;
  });
  // Don't show chat panel on Insights page (it has its own chat interface)
  const isInsightsPage = location.pathname === '/insights';

  useEffect(() => {
    if (!isInsightsPage) return;
    dispatch(setChatOpen(false));
    dispatch(setHistoryPanelOpen(false));
  }, [isInsightsPage, dispatch]);

  const captureScreenshot = async () => {
    try {
      // Find the main content wrapper (the actual page content, not the overlay)
      const contentWrapper = document.getElementById('content-wrapper');
      if (!contentWrapper) {
        throw new Error('Content wrapper not found');
      }

      // Temporarily hide chat-related elements for clean screenshot
      const chatPanel = document.querySelector('[data-chat-panel]');
      const chatToggle = document.querySelector('[data-chat-toggle]');
      const originalChatPanelDisplay = chatPanel?.style.display;
      const originalToggleDisplay = chatToggle?.style.display;

      if (chatPanel) {
        chatPanel.style.display = 'none';
      }
      if (chatToggle) {
        chatToggle.style.display = 'none';
      }

      // Small delay to ensure elements are hidden
      await new Promise((resolve) => {
        return setTimeout(resolve, 50);
      });

      // Optimize screenshot capture for performance
      // Use lower scale and optimized options to reduce lag
      const canvas = await html2canvas(contentWrapper, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        scale: 0.5, // Reduce scale for better performance (0.5 = half resolution, still good quality)
        logging: false,
        removeContainer: false,
        imageTimeout: 0,
        ignoreElements: (element) => {
          // Ignore elements that might cause performance issues
          return (
            element.classList?.contains('chat-panel') ||
            element.classList?.contains('chat-toggle') ||
            element.id === 'chat-panel'
          );
        },
      });

      // Restore chat elements immediately
      if (chatPanel) {
        chatPanel.style.display = originalChatPanelDisplay || '';
      }
      if (chatToggle) {
        chatToggle.style.display = originalToggleDisplay || '';
      }

      return canvas;
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      throw error;
    }
  };

  return (
    <>
      {!isInsightsPage && authenticationStatus.aiChatEnabled && isOpen && overlayDimEnabled && (
        <Box
          data-chat-panel
          role='presentation'
          aria-hidden
          onClick={() => {
            dispatch(setChatOpen(false));
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1299,
            pointerEvents: 'auto',
            cursor: 'pointer',
          }}
        />
      )}
      {!isInsightsPage && authenticationStatus.aiChatEnabled && isOpen && (
        <ChatPanel onScreenshot={captureScreenshot} />
      )}
    </>
  );
}

export default ChatWrapper;
